import { z } from "zod";

/**
 * Zod schema for the batch-upload file picker — the payload handed to
 * `batchService.uploadBatch()`. Per Hector's kickoff spec, form inputs are
 * Zod-validated; this is the API-payload schema (the ADR-010 pairing:
 * `service.upload(schema.parse(values))`).
 *
 * A single file picker isn't a `<Field>`-based form, so it doesn't go through
 * `src/forms/<Form>` (RHF) — but it still gets a Zod schema as its single
 * source of truth, validated via `safeParse` here and again in the service.
 */

/** 10 MB — generous for an XLS of names; the real cap is the backend's call. */
const MAX_BYTES = 10 * 1024 * 1024;
// .csv included: the downloadable template is a .csv, so it must be uploadable too (QA 2026-06-16).
const ACCEPTED_EXTENSIONS = [".csv", ".xls", ".xlsx"] as const;

export const batchUploadSchema = z.object({
  file: z
    .instanceof(File)
    .refine((f) => f.size > 0, { message: "This file is empty" })
    .refine((f) => f.size <= MAX_BYTES, { message: "File must be 10 MB or smaller" })
    .refine((f) => ACCEPTED_EXTENSIONS.some((ext) => f.name.toLowerCase().endsWith(ext)), {
      message: "Upload a .csv, .xls, or .xlsx file",
    }),
});

export type BatchUploadInput = z.infer<typeof batchUploadSchema>;

/**
 * Validate a single picked file for inline feedback. Returns the first error
 * message, or null when the file is valid.
 */
export function validateBatchFile(file: File | null | undefined): string | null {
  const res = batchUploadSchema.safeParse({ file });
  return res.success ? null : (res.error.issues[0]?.message ?? "Invalid file");
}
